import type { JSX } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { FK, panel, RADIUS } from './tokens';

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  suffix?: ReactNode;
  /** Строка под значением («3 просрочено», «+2 за неделю»). */
  hint?: ReactNode;
  /** Цвет подсказки (например, красный — есть просрочки). */
  hintColor?: string;
  /** Иконка в цветном квадрате 36×36 слева. */
  icon?: ReactNode;
  /** Цвет иконки и её подложки (по умолчанию оранжевый). */
  tone?: 'orange' | 'blue' | 'green' | 'red' | 'gold' | 'indigo';
  /** Карточка-ссылка (переход к списку). */
  to?: string;
  style?: CSSProperties;
}

const TONES: Record<NonNullable<StatCardProps['tone']>, [string, string]> = {
  orange: ['var(--fk-orange-soft)', 'var(--fk-orange)'],
  blue: ['var(--fk-info-soft)', 'var(--fk-info)'],
  green: ['var(--fk-success-soft)', 'var(--fk-success-text)'],
  red: ['var(--fk-error-soft)', 'var(--fk-error)'],
  gold: ['var(--fk-warning-soft)', 'var(--fk-warning-text)'],
  indigo: ['rgba(37,29,74,.08)', 'var(--fk-indigo)'],
};

/**
 * Плитка-счётчик (порт `_ds/components/StatCard`): подпись, крупное число
 * 26/800, подсказка. Для ряда «4 плитки по роли» на главной и шапок разделов;
 * с дельтой к периоду — `KpiCard`.
 */
export function StatCard({ label, value, suffix, hint, hintColor, icon, tone = 'orange', to, style }: StatCardProps): JSX.Element {
  const [bg, fg] = TONES[tone];
  const inner = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      {icon ? (
        <span style={{ width: 36, height: 36, borderRadius: RADIUS.md, background: bg, color: fg, display: 'grid', placeItems: 'center', fontSize: 17, flexShrink: 0 }}>
          {icon}
        </span>
      ) : null}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: FK.muted, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.5px', color: FK.text, fontVariantNumeric: 'tabular-nums' }}>
          {value}
          {suffix ? <span style={{ fontSize: 15, fontWeight: 500, color: FK.text2, marginLeft: 4 }}>{suffix}</span> : null}
        </div>
        {hint ? <div style={{ fontSize: 12, marginTop: 6, color: hintColor ?? FK.text2 }}>{hint}</div> : null}
      </div>
    </div>
  );
  const box: CSSProperties = { ...panel, padding: '14px 18px', minWidth: 0, display: 'block', color: 'inherit', ...style };
  return to ? (
    <Link to={to} className="fk-tile" style={{ ...box, textDecoration: 'none' }}>
      {inner}
    </Link>
  ) : (
    <div style={box}>{inner}</div>
  );
}
